import { useState } from "react";
import { SignUpButton, useAuth } from "@clerk/clerk-react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import {
  LucideChevronLeft,
  LucideChevronRight,
  LucideCheck,
  LucideX,
  LucideUsers,
  LucideSettings2,
  LucideDownload,
  LucidePenLine,
} from "lucide-react";
import { BACKEND_URL } from "@/constants/api";
import { PDFPreview } from "./PDFPreview";
import { ReportFormHeader } from "./ReportFormHeader";
import { StudentListSheet } from "@/components/modals/StudentListSheet";
import { UnsavedChangesModal } from "@/components/modals/UnsavedChangesModal";

function parseReportFolderContext(backUrl) {
  if (!backUrl) return null;
  const ownedMatch = backUrl.match(/^\/report-folders\/([^/]+)$/);
  if (ownedMatch) return { kind: "owned", id: ownedMatch[1] };
  const sharedMatch = backUrl.match(/^\/shared-report-folders\/([^/]+)$/);
  if (sharedMatch) return { kind: "shared", id: sharedMatch[1] };
  return null;
}

/**
 * Shared layout shell for all report form pages.
 *
 * Renders the header bar, shared/defaults banners, step indicator,
 * preset toolbar, form content area, navigation buttons, PDF preview panel,
 * and the demo post-download CTA modal.
 *
 * Props:
 *   hook              - Return value from useReportForm()
 *   title             - Report card title (e.g. "Ontario Progress Report Card")
 *   reportId          - Report ID (null for demo)
 *   backUrl           - URL for the Back button
 *   contextInfo       - { grade, schoolYear, oen, studentName }
 *   defaultsGroupId   - Report Folder ID when editing Report Folder defaults
 *   onRequestSave     - Callback for "Save Defaults" button
 *   STEPS             - Step definitions array
 *   PRESETS            - Preset data array for demo toolbar
 *   defaultPageCount  - Number of PDF pages (for preview skeleton)
 *   StepIndicator     - Step indicator component
 *   stepIndicatorProps - Extra props to pass to StepIndicator
 *   renderStep        - (form, currentStep) => JSX for the active step content
 *   formKey           - Optional key on the <form> element (used by provincial reports)
 *   formContentClassName - Optional className for the step content scroll area
 */
export function ReportFormShell({
  hook,
  title,
  reportId,
  backUrl,
  contextInfo,
  defaultsGroupId,
  onRequestSave,
  STEPS,
  PRESETS,
  defaultPageCount = 2,
  StepIndicator,
  stepIndicatorProps = {},
  renderStep,
  formKey,
  formContentClassName = "flex-1 overflow-y-auto px-6 pb-4",
  readOnly = false,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { getToken } = useAuth();
  const {
    form,
    currentStep,
    setCurrentStep,
    filledPDF,
    isPdfGenerating,
    showPreview,
    setShowPreview,
    showPostDownloadModal,
    setShowPostDownloadModal,
    isDirty,
    isSaving,
    hasSavedOnce,
    scrollTargets,
    completedSteps,
    formPaneRef,
    handleNext,
    handlePrev,
    handleDownload,
    toggleManualComplete,
    triggerPdfRegen,
    flushSaveRef,
    blocker,
    signature,
  } = hook;

  const isShared = backUrl?.startsWith("/shared-report-folders");

  const reportFolderContext = parseReportFolderContext(backUrl);
  const canShowStudents = !!reportId && !!reportFolderContext;
  const [studentsOpen, setStudentsOpen] = useState(false);
  const [students, setStudents] = useState(null);
  const [studentsLoading, setStudentsLoading] = useState(false);

  async function fetchStudents() {
    if (!reportFolderContext) return;
    setStudentsLoading(true);
    try {
      const token = await getToken();
      const url =
        reportFolderContext.kind === "owned"
          ? `${BACKEND_URL}api/report-folders/${reportFolderContext.id}?limit=0`
          : `${BACKEND_URL}api/shared-report-folders/${reportFolderContext.id}?limit=100`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("Failed to load students");
      const data = await res.json();
      setStudents(data.reports || []);
    } catch {
      setStudents([]);
    } finally {
      setStudentsLoading(false);
    }
  }

  function openStudents() {
    if (students === null && !studentsLoading) fetchStudents();
    setStudentsOpen(true);
  }

  function getStudentLinkPath(card) {
    if (!reportFolderContext) return undefined;
    return reportFolderContext.kind === "owned"
      ? `/report-folders/${reportFolderContext.id}/reports/${card._id}`
      : `/shared-report-folders/${reportFolderContext.id}/reports/${card._id}`;
  }

  const studentLinkState =
    reportFolderContext?.kind === "shared"
      ? { permission: location.state?.permission ?? "view" }
      : undefined;

  function handleSelectStudent(card) {
    setStudentsOpen(false);
    if (card._id === reportId) return;
    flushSaveRef.current?.();
  }

  async function handleToggleComplete(card) {
    const previous = card.checkedState ?? "incomplete";
    const next = previous === "complete" ? "incomplete" : "complete";
    setStudents((prev) =>
      prev?.map((r) => (r._id === card._id ? { ...r, checkedState: next } : r)) ?? prev,
    );
    try {
      const token = await getToken();
      const res = await fetch(`${BACKEND_URL}api/reports/${card._id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ checkedState: next }),
      });
      if (!res.ok) throw new Error("Update failed");
    } catch {
      setStudents((prev) =>
        prev?.map((r) => (r._id === card._id ? { ...r, checkedState: previous } : r)) ?? prev,
      );
      toast.error("Failed to update status. Please try again.");
    }
  }

  // Only show save status for persisted reports (not demo or defaults mode)
  const saveStatus = reportId
    ? (isSaving || isDirty ? "saving" : hasSavedOnce.current ? "saved" : null)
    : null;

  function handleBack() {
    flushSaveRef.current?.();
    navigate(backUrl || "/");
  }

  return (
    <div className="h-[calc(100vh-4rem)] flex flex-col font-sans">
      {/* Header bar */}
      <ReportFormHeader
        onBack={handleBack}
        title={title}
        saveStatus={saveStatus}
        contextInfo={contextInfo}
        showPreview={showPreview}
        onTogglePreview={() => setShowPreview((v) => !v)}
        isPdfGenerating={isPdfGenerating}
        filledPDF={filledPDF}
        onDownload={handleDownload}
        defaultsGroupId={defaultsGroupId}
        onSaveDefaults={() => onRequestSave?.(form.getValues())}
        onShowStudents={canShowStudents ? openStudents : undefined}
      />

      {canShowStudents && (
        <StudentListSheet
          open={studentsOpen}
          onOpenChange={setStudentsOpen}
          students={students ?? []}
          loading={studentsLoading}
          currentReportId={reportId}
          getLinkPath={getStudentLinkPath}
          linkState={studentLinkState}
          onSelect={handleSelectStudent}
          onToggleComplete={handleToggleComplete}
          canEdit={reportFolderContext?.kind === "owned"}
        />
      )}

      {/* Shared Report Folder banner */}
      {isShared && (
        <div className="flex items-center gap-2 px-5 py-1.5 bg-warning-bg border-b border-warning-border text-xs text-warning-fg shrink-0">
          <LucideUsers size={12} className="shrink-0" />
          {readOnly
            ? "View only - you can view and download this report but cannot make changes."
            : "Shared Report Folder - changes you save are visible to the owner"}
        </div>
      )}

      {/* Report Folder Defaults banner */}
      {defaultsGroupId && (
        <div className="flex items-center gap-2 px-5 py-2.5 bg-info-bg border-b-2 border-info-border shrink-0">
          <LucideSettings2 size={15} className="shrink-0 text-info-fg" />
          <span className="text-sm font-medium text-info-fg">
            Report Folder Defaults - <span className="font-normal">You&apos;re setting default values for all reports in this Report Folder. No individual student is being edited.</span>
          </span>
        </div>
      )}

      {/* Main content */}
      <div className="flex flex-1 overflow-hidden">
        {/* Form panel */}
        <div className={`flex flex-col overflow-hidden transition-all duration-300 ${showPreview ? "w-[55%]" : "w-full max-w-4xl mx-auto"}`}>
          {/* Step indicator */}
          <div className="px-6 pt-5 pb-3 shrink-0">
            <StepIndicator
              currentStep={currentStep}
              completedSteps={completedSteps}
              onStepClick={setCurrentStep}
              {...stepIndicatorProps}
            />
          </div>

          {/* Demo preset toolbar */}
          {!reportId && !defaultsGroupId && PRESETS && PRESETS.length > 0 && (
            <div className="px-6 py-2 shrink-0 flex flex-wrap gap-2 border-b border-border bg-muted/50">
              {PRESETS.map((preset) => (
                <Button
                  key={preset.label}
                  type="button"
                  variant="outline"
                  size="sm"
                  className="text-xs transition-all duration-150 hover:scale-105 hover:shadow-md active:scale-95"
                  onClick={() => { form.reset(preset.data); triggerPdfRegen(); }}
                >
                  {preset.label}
                </Button>
              ))}
            </div>
          )}

          {/* Step content */}
          <div ref={formPaneRef} className={formContentClassName}>
            <div
              inert={readOnly ? "" : undefined}
              style={readOnly ? { opacity: 0.75, pointerEvents: "none" } : undefined}
            >
              <Form {...form}>
                <form key={formKey}>
                  {renderStep(form, currentStep)}
                </form>
              </Form>
            </div>
          </div>

          {/* Navigation buttons */}
          <div className="flex items-center justify-between px-6 py-3 border-t border-border bg-card shrink-0">
            <Button type="button" size="sm" variant="outline" onClick={handlePrev} disabled={currentStep === 1}>
              <LucideChevronLeft size={14} className="mr-1" />
              Prev
            </Button>
            <div className="flex items-center gap-2">
              {!isShared && !readOnly && (
                <Button
                  type="button"
                  size="sm"
                  variant={completedSteps.has(currentStep) ? "default" : "outline"}
                  onClick={() => toggleManualComplete(currentStep)}
                  className={completedSteps.has(currentStep) ? "gap-1.5 bg-success hover:bg-success/90 text-success-foreground border-success" : "gap-1.5"}
                >
                  <LucideCheck size={14} />
                  {completedSteps.has(currentStep) ? "Marked Complete" : "Mark Complete"}
                </Button>
              )}
              {signature?.canSignTeacher && !readOnly && (
                signature.signedState.teacher ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => signature.unsignAs("teacher")}
                    disabled={signature.signBusy}
                    className="gap-1.5 border-success text-success hover:bg-success/10"
                    title="Click to remove your signature"
                  >
                    <LucideCheck size={14} /> Signed
                  </Button>
                ) : (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => signature.signAs("teacher")}
                    disabled={signature.signBusy}
                    className="gap-1.5"
                    title={signature.hasOwnSignature ? "Sign this report" : "Set up your signature first"}
                  >
                    <LucidePenLine size={14} /> Sign
                  </Button>
                )
              )}
              {currentStep < STEPS.length ? (
                <Button type="button" size="sm" onClick={handleNext}>
                  Next
                  <LucideChevronRight size={14} className="ml-1" />
                </Button>
              ) : (
                <Button type="button" size="sm" onClick={handleDownload} disabled={!filledPDF || isPdfGenerating}>
                  <LucideDownload size={14} className="mr-1.5" />
                  {isPdfGenerating ? "Updating…" : "Download PDF"}
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* PDF preview panel */}
        <div className={`border-l border-border transition-all duration-300 overflow-hidden ${showPreview ? "w-[45%]" : "w-0"}`}>
          {showPreview && (
            <PDFPreview
              src={filledPDF}
              scrollTarget={scrollTargets[currentStep]}
              defaultPageCount={defaultPageCount}
            />
          )}
        </div>
      </div>

      {/* Unsaved-changes guard for in-app navigation */}
      <UnsavedChangesModal
        open={blocker?.state === "blocked"}
        onStay={() => blocker?.reset?.()}
        onLeave={() => {
          flushSaveRef.current?.();
          blocker?.proceed?.();
        }}
      />

      {/* Demo CTA overlay */}
      {showPostDownloadModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="relative bg-card rounded-2xl shadow-2xl max-w-sm w-full mx-4 overflow-hidden">
            <div className="p-6 pt-5">
              <button
                onClick={() => setShowPostDownloadModal(false)}
                className="absolute top-4 right-4 text-muted-foreground hover:text-foreground transition-colors"
                aria-label="Close"
              >
                <LucideX size={16} />
              </button>
              <h2
                className="text-xl font-semibold text-foreground leading-snug mb-4 font-display"
              >
                Download Complete! <br /> Take a look :)
              </h2>
              <div className="border-t border-border mb-4" />
              <SignUpButton mode="redirect">
                <button
                  className="w-full py-2.5 px-4 rounded-xl text-sm font-semibold text-white transition-all duration-150 hover:opacity-90 active:scale-[0.98] bg-gradient-to-br from-[hsl(var(--brand-900))] to-[hsl(var(--brand-700))]"
                >
                  Create free account
                </button>
              </SignUpButton>
              <ul className="mt-3 space-y-1.5">
                {[
                  "Collaborate with teachers to complete reports",
                  "Use Gen AI to support your comments",
                  "Automate dozens of fields - no manual typing",
                  "And much more\u2026",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2 text-xs text-muted-foreground">
                    <span className="mt-0.5 shrink-0 w-3.5 h-3.5 rounded-full bg-primary/5 flex items-center justify-center">
                      <LucideCheck size={8} className="text-primary" strokeWidth={3} />
                    </span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
